import { Immutable } from "immer";
import React from "react";

import { templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { Carousel } from "../../blocks/Carousel";
import { CompareMediaSection } from "../../blocks/CompareMediaSection";
import { FindRetailerAdvert } from "../../blocks/FindRetailerAdvert";
import { InformationHeroBanner } from "../../blocks/InformationHeroBanner";
import { MediaSection } from "../../blocks/MediaSection";
import { QuestionAnswerList } from "../../blocks/QuestionAnswerList";
import { StoreLocator } from "../../blocks/StoreLocator";
import { StoreLocatorForm } from "../../blocks/StoreLocatorForm";
import { VideoMediaSection } from "../../blocks/VideoMediaSection";
import { WhyMediaSection } from "../../blocks/WhyMediaSection";
import { toolboxComponentsMap } from "../../blocks/toolboxComponentsMap";

/**
 * sealy InformationPage body assembly (#33585): dispatches every
 * ``InformationPageBodyContent`` block (the 30 Toolbox keys + 10 legacy
 * information blocks) in one React render tree.
 *
 * FAITHFUL port of the legacy ``cms/information_page.html`` body render
 * (verified at git dev): a bare ``{% for block in self.body %}{% include_block
 * block %}{% endfor %}`` loop -- NO per-block Wagtail ``w-block-*`` wrapper,
 * reproduced with ``tagName={React.Fragment}`` (same as the HomePage
 * assembly).
 *
 * All 10 legacy keys are now real ported components -- no
 * legacy pre-rendered HTML strings remain on this page. The 4
 * value-less StaticBlocks were ported in #33285 batch A; the hero/media/qna
 * StructBlocks in batch B; ``carousel`` in batch C.
 * That includes ``store_locator_form``, whose rendered markup is the
 * ``data-place-react="retail-locator"`` mount point: the component emits it
 * exactly as the legacy path did, so the ``dynamicPlaceComponent`` scanner in
 * ``client/src/RetailLocator`` keeps mounting the locator app into it.
 */
export const informationPageComponentsMap = {
    ...toolboxComponentsMap,
    hero_banner: InformationHeroBanner,
    media_section: MediaSection,
    video_media_section: VideoMediaSection,
    why_pp_media_section: WhyMediaSection,
    compare_media_section: CompareMediaSection,
    carousel: Carousel,
    questions_and_answers: QuestionAnswerList,
    store_locator_advert: StoreLocator,
    store_locator_form: StoreLocatorForm,
    find_retailer_cocoon_advert: FindRetailerAdvert,
} as const;

export const InformationPageBody = (
    props: Immutable<{ page: templates.InformationPageTemplate["page"] }>,
): React.JSX.Element => {
    // `body` is non-null (the model field is `blank=True` without
    // `null=True`); an empty stream renders nothing.
    return (
        <StreamField
            tagName={React.Fragment}
            value={props.page.body.value}
            components={informationPageComponentsMap}
        />
    );
};
